/* eslint-disable max-lines */
import { useAtom, useAtomValue } from "jotai";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { useArtworkForm } from "@design-lab/entities/artwork";
import { useFormattedDeliveryDate } from "@design-lab/entities/composition/model";
import { DesignArtwork } from "@design-lab/features/design-artwork";
import { PlacementControlTooltip } from "@design-lab/features/design-artwork/ui/PlacementControlTooltip";
import { ManageProductSizing } from "@design-lab/features/manage-product-sizing/ui/ManageProductSizing";
import { SelectProductColor } from "@design-lab/features/select-product-color";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { cn } from "@design-lab/shared/lib";
import { Button } from "@design-lab/shared/ui";
import { type AppTab, type Tab } from "@design-lab/shared/ui/layouts/state";
import { UploadSection } from "@design-lab/shared/ui/layouts/UploadSection";
import { useArtboardVisibility } from "@design-lab/shared/ui/layouts/useArtboardVisibility";
import { Tabs, TabsContent, TabsTrigger, TabsList } from "@design-lab/shared/ui/tabs";
import { DesignLabArtboard } from "@design-lab/widgets";
import { usePlacementPosition } from "@design-lab/widgets/artboard";
import {
  mobileDrawerExpandedAtom,
  mobileSizePlacementOpenAtom
} from "@design-lab/widgets/artboard/model/state";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { ImagePlacement, ImageSize } from "@design-lab/widgets/design-lab-upload-artwork";
import { Header } from "@design-lab/widgets/navigation";
import { SummaryTab } from "@design-lab/widgets/summary";
import { isSummaryViewAtom } from "@design-lab/widgets/summary/model/state";
import { useShowWeaveDesignLab } from "@design-lab/shared/lib/config";
import { AddToCart } from "@design-lab/widgets/footer-desktop/ui/AddToCart";
import { SaveProject } from "@design-lab/features/save-project";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { NextButton } from "@design-lab/widgets/navigation/ui/NextButton";

const EXPANDED_HEIGHT = 40; // vh
const COLLAPSED_HEIGHT = 38; // px

export function DesignLabMobile({
  handleTabClick,
  tabs,
  tab
}: {
  handleTabClick: (value: Tab) => void;
  tabs: AppTab[];
  tab: Tab;
}) {
  const { lens } = useArtworkForm();
  const currentPlacementLens = lens.focus("currentPlacement");
  const { placement } = usePlacementPosition(currentPlacementLens);
  const { canGoNext } = useTab();

  const { isSaveProjectVisible } = useSaveProject();

  const placementLens = lens.focus(placement);

  const side = placement === "back" ? "back" : "front";

  const deliveryDateInfo = useFormattedDeliveryDate();
  const drawerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevTabRef = useRef(tab);
  const { shouldHideArtboard } = useArtboardVisibility(tab);
  const shouldShowWeaveDesignLab = useShowWeaveDesignLab();
  const { currentIndex } = useTabNavigation();
  const [isSizePlacementOpen, setIsSizePlacementOpen] = useAtom(mobileSizePlacementOpenAtom);

  const [isExpanded, setIsExpanded] = useAtom(mobileDrawerExpandedAtom);

  const isSummaryView = useAtomValue(isSummaryViewAtom);

  const isUploadTab = tab === "upload";

  const handleTabHeaderClick = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (prevTabRef.current !== tab) {
      setIsExpanded(true);
      setIsSizePlacementOpen(false);
    }
    prevTabRef.current = tab;
  }, [tab, setIsSizePlacementOpen, setIsExpanded]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("drawer-expanded", String(isExpanded));
    }
  }, [isExpanded]);

  const currentHeight = isExpanded ? `${EXPANDED_HEIGHT}vh` : `${COLLAPSED_HEIGHT}px`;

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-gray-100">
      <Header deliveryDateInfo={deliveryDateInfo} />

      {isSummaryView ? (
        <div className="flex flex-1 flex-col overflow-hidden">
          <SummaryTab />
        </div>
      ) : (
        <Tabs
          value={tab}
          className={`flex flex-col overflow-hidden ${shouldHideArtboard ? "h-full" : "relative flex-1"}`}
        >
          <div
            className={
              shouldHideArtboard ? "invisible absolute h-0 w-0 overflow-hidden" : "flex-1 p-1 pb-0"
            }
          >
            <DesignLabArtboard key={String(shouldHideArtboard)} />
          </div>
          {isSizePlacementOpen ? (
            <div className="bg-background mx-1 space-y-4 rounded-t-2xl p-2 shadow-lg">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">Size & Placement</p>
                <Button
                  variant="icon"
                  className="p-2"
                  onPress={() => setIsSizePlacementOpen(false)}
                >
                  <X className="size-6" />
                </Button>
              </div>
              <div className="flex gap-6">
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Design Size</p>
                  <ImageSize lens={placementLens.focus("size")} side={side} />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center">
                    <p className="text-sm font-semibold">Placement</p>
                    <PlacementControlTooltip isDesktop={false} />
                  </div>
                  <ImagePlacement />
                </div>
              </div>
            </div>
          ) : (
            <div
              ref={containerRef}
              className={cn(
                "bg-background mx-1 flex flex-col overflow-hidden rounded-t-2xl border p-2 pt-0 transition-[height] duration-300 ease-out",
                shouldHideArtboard && "flex-1"
              )}
              style={
                shouldHideArtboard
                  ? {}
                  : {
                      height: currentHeight,
                      minHeight: `${COLLAPSED_HEIGHT}px`,
                      maxHeight: `${EXPANDED_HEIGHT}vh`
                    }
              }
            >
              <div
                className="flex h-9 items-center justify-between gap-2"
                onClick={handleTabHeaderClick}
              >
                <p className="text-sm font-medium">
                  {currentIndex + 1}. {tabs[currentIndex]?.mobileAccordionTitle}
                </p>
                {(!shouldShowWeaveDesignLab || (!isUploadTab && shouldShowWeaveDesignLab)) && (
                  <Button
                    variant="icon"
                    onPress={handleTabHeaderClick}
                    aria-label="Close"
                    className="p-0"
                    data-testid="close-expanded-tabs-button"
                  >
                    {isExpanded ? (
                      <ChevronDown className="size-6" />
                    ) : (
                      <ChevronUp className="size-6" />
                    )}
                  </Button>
                )}
              </div>

              <div
                ref={drawerRef}
                className={cn(
                  "transition-height h-0 overflow-y-auto duration-300 ease-out",
                  (isExpanded || shouldHideArtboard) && "h-full"
                )}
              >
                <TabsContent value="upload" className="mt-0 h-full">
                  <UploadSection />
                </TabsContent>
                <TabsContent value="products" className="mt-0 h-full">
                  <SelectProductColor />
                </TabsContent>
                <TabsContent value="size" className="mt-0 h-full">
                  <ManageProductSizing />
                </TabsContent>
                <TabsContent value="front" className="mt-0 h-full">
                  <DesignArtwork side="front" title="4. Design Front" />
                </TabsContent>
                <TabsContent value="back" className="mt-0 h-full">
                  <DesignArtwork side="back" title="5. Design Back" />
                </TabsContent>
              </div>
              <NextButton className="h-12 w-full bg-blue-700 hover:bg-blue-800" />
            </div>
          )}

          {!canGoNext && (
            <div className="flex flex-col gap-2 px-2 py-1">
              <AddToCart />
            </div>
          )}

          <TabsList className="flex-0 z-30 h-auto flex-row gap-1">
            {tabs.map((item, index) => (
              <TabsTrigger
                data-testid={`tab-${item.value}-trigger`}
                key={item.title}
                value={item.value}
                asChild
              >
                <Button
                  data-testid={`tab-${item.value}-button`}
                  variant="secondary"
                  isDisabled={!item.enabled}
                  onPress={() => {
                    if (tab === item.value) {
                      setIsExpanded(!isExpanded);
                    } else {
                      handleTabClick(item.value);
                    }
                  }}
                  className={cn(
                    "bg-background relative h-auto w-full flex-col gap-1 border px-2 text-xs shadow-md",
                    isExpanded &&
                      "data-[state=active]:border-blue-600 data-[state=active]:text-blue-600"
                  )}
                >
                  {item.icon}
                  {item.enabled && item.checked && (
                    <div className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-blue-600">
                      <Check className="text-background h-3 w-3" />
                    </div>
                  )}
                  <span>
                    {index + 1}. {item.mobileTitle}
                  </span>
                </Button>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {isSaveProjectVisible && <SaveProject />}
    </div>
  );
}
